import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import DebugRender from 'components/Debug/Render/DebugRender';
import { ff } from 'helpers/featureFlags';
import { AttributeAnchors } from 'components/Anchor/Anchor';
import isEqual from 'lodash/isEqual';
import { ascii, globalFilter } from 'constants/patterns';
import { DeepSafePartial } from 'types/types';
import { FormError } from '@redux/modules/form/types';
import { stateFormErrorConnector } from '../shared/stateFormErrors';
import ValidationElement from '../ValidationElement';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';

export const autotab = (event, maxlength, back, next) => {
  const input = event.target;

  // If there is a selection (highlighted text) within the element then we
  // need to let normal operations take precedence.
  const highlighted = (input.selectionEnd || 0) - (input.selectionStart || 0) !== 0;
  if (highlighted) {
    return;
  }

  const value = input.value;
  const code = event.keyCode;
  const backCodes = [8, 46];
  const nextCodes = [
    // 0 through 9
    48,
    49,
    50,
    51,
    52,
    53,
    54,
    55,
    56,
    57,

    // 0 through 9 on the numpad
    96,
    97,
    98,
    99,
    100,
    101,
    102,
    103,
    104,
    105,

    // a through z
    65,
    66,
    67,
    68,
    69,
    70,
    71,
    72,
    73,
    74,
    75,
    76,
    77,
    78,
    79,
    80,
    81,
    82,
    83,
    84,
    85,
    86,
    87,
    88,
    89,
    90,
  ];

  if (backCodes.includes(code) && value.length < 1) {
    back();
  } else if (nextCodes.includes(code) && value.length >= parseInt(maxlength, 10)) {
    next();
  }
};

/**
 * Find the closest element matching the query selector
 * @param {Element} el
 * @param {string} selector
 * @returns {Element | null}
 */
export const closest = (el, selector) => {
  let matches;
  [
    'matches',
    'webkitMatchesSelector',
    'mozMatchesSelector',
    'msMatchesSelector',
    'oMatchesSelector',
  ].some((fn) => {
    if (typeof document.body[fn] === 'function') {
      matches = fn;
      return true;
    }
    return false;
  });

  let parent;
  let current = el;
  while (current) {
    parent = current.parentElement;
    if (parent && parent[matches](selector)) {
      return parent;
    }
    current = parent;
  }
  return null;
};

// Find the closes element (field) with an aria-label applied
export const ariaLabel = (el) => {
  const nearest = closest(el, '.field');
  if (nearest) {
    return nearest.getAttribute('aria-label');
  }
  return null;
};

interface Props extends ValidationElementProps, Partial<HTMLInputElement>, Partial<FormError> {
}
export class Generic extends ValidationElement<Props> {
  constructor(props) {
    super(props);

    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      value: props.value,
      focus: props.focus,
      error: props.error,
      valid: props.valid,
    };

    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.disallowClipboard = this.disallowClipboard.bind(this);
    this.errors = this.errors.bind(this);
    this.input = React.createRef();
    this.label = React.createRef();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    let updates = {};

    if (nextProps.disabled !== this.props.disabled || !isEqual(this.props.value, nextProps.value)) {
      // If disabled, we clear the value and clear state
      if (nextProps.disabled && this.state.value !== '') {
        updates = { value: '', valid: null, error: null };
        this.setState(updates);
      }

      if (isEqual(this.props.value, nextProps.value) || this.state.value === nextProps.value) {
        return;
      }

      if (nextProps.value !== this.state.value) {
        updates = { ...updates, value: nextProps.value };
      }

      if (updates.value !== undefined && updates.value !== this.state.value) {
        const errors = this.errors(updates.value, nextProps);
        updates.error = errors.some((x) => x.valid === false);
        updates.valid = errors.every((x) => x.valid === true);
      }

      this.setState(updates);
    }
  }

  componentDidUpdate(prevProps) {
    // Written specifically for Email fields that are dependent on each other
    // If a fields requiredness changes, check validations again.
    if (this.props.required !== prevProps.required) {
      this.handleValidation();
    }
    // revalidate when maxlength changes, usually from name initials
    if (this.props.maxlength && this.props.maxlength !== prevProps.maxlength) {
      this.handleValidation();
    }
  }

  /**
   * This allows us to switch logic of "hasError" between state.application and
   * state.form. When useStateFormErrors is true, we'll use
   * this.props.hasStateFormError. Otherwise, continue to use the previous error
   * handling.
   */
  hasError = () => {
    if (this.props.forceValidation !== true && ff(this.props.features).getActive('EN-4733')) {
      return this.props.hasStateFormError;
    }
    return this.state.error;
  };

  /**
   * This allows us to switch logic of "isValid" between state.application and
   * state.form. When useStateFormErrors is true, we'll use
   * this.props.hasStateFormError. Otherwise, continue to use the previous error
   * handling.
   */
  isValid = () => {
    if (this.props.forceValidation !== true && ff(this.props.features).getActive('EN-4733')) {
      return !this.props.hasStateFormError && this.state.value.length > 0;
    }
    return this.state.valid;
  };

  errors(value, props = this.props) {
    if (this.props.forceValidation !== true && ff(this.props.features).getActive('EN-4733')) { return []; }

    return (
      this.props.onError(
        value,
        this.props.additionalLengthErrors
          ? this.constructor.errors
            .map((err) => ({
              code: err.code,
              valid: err.func(value, props),
              uid: this.state.uid,
            }))
            .filter((err) => err.code !== 'length')
          : this.constructor.errors
            .map((err) => ({
              code: err.code,
              valid: err.func(value, props),
              uid: this.state.uid,
            }))
            .filter((err) => err.code !== 'length.tooshort' && err.code !== 'length.toolong')
      ) || []
    );
  }

  /**
   * Filters input values from a string by removing
   * all characters matched by the provided regex.
   *
   * @param {string} value
   * @param {RegExp} regex
   * @returns {string}
   */
  filterInput(value, regex) {
    let newValue = value;

    if (regex?.test(newValue)) {
      newValue = newValue.replace(regex, '');
    }

    return newValue;
  }

  /**
   * Handle the change event.
   */
  handleChange(event) {
    const newEvent = cloneDeep(event);

    newEvent.target.value = this.filterInput(newEvent.target.value, ascii);
    newEvent.target.value = this.filterInput(newEvent.target.value, globalFilter);

    if (this.props.inputFilter instanceof RegExp) {
      newEvent.target.value = this.filterInput(newEvent.target.value, this.props.inputFilter);
    }

    this.setState(
      {
        value: newEvent.target.value,
      },
      () => {
        super.handleChange(newEvent);
      }
    );
  }

  /**
   * Handle the focus event.
   */
  handleFocus(event) {
    this.setState(
      {
        focus: true,
      },
      () => {
        super.handleFocus(event);
      }
    );
  }

  /**
   * Handle the blur event.
   */
  handleBlur(e) {
    const { value } = this.state;
    const { setToUpperCaseOnBlur } = this.props;
    const event = setToUpperCaseOnBlur ? cloneDeep(e) : e;
    if (setToUpperCaseOnBlur && event?.target?.value) {
      event.target.value = event.target.value.toUpperCase();
    }
    this.setState(
      {
        focus: false,
        value: setToUpperCaseOnBlur ? `${value}`.trim().toUpperCase() : `${value}`.trim(),
      },
      () => {
        super.handleChange(event);
        super.handleBlur(event);
      }
    );
  }

  /**
   * Execute validation checks on the value.
   */
  handleValidation() {
    // eslint-disable-next-line
    const errors = this.errors(`${this.state.value}`.trim());

    const validationState = this.props.makeStateFromErrors
      ? this.props.makeStateFromErrors(errors)
      : { error: errors.some((x) => x.valid === false), valid: errors.every((x) => x.valid === true) };

    this.setState(validationState);
  }

  /**
   * Handle the key down event.
   */
  handleKeyDown(event) {
    this.props.onKeyDown?.(event);// eslint-disable-line no-unused-expressions
    autotab(event, this.props.maxlength, this.props.tabBack, this.props.tabNext);
  }

  /**
   * Prevents clipboard events from making changes to the value of the elements
   */
  disallowClipboard(event) {
    event.preventDefault();
  }

  /**
   * Style classes applied to the wrapper.
   */
  getWrapperClassName() {
    return `${this.props.className || ''} ${!this.props.disabled && this.hasError() ? 'usa-input-error' : ''
    }`.trim();
  }

  /**
   * Style classes applied to the label element.
   */
  labelClass() {
    if (this.props.disabled) {
      return 'disabled';
    }

    return `${this.hasError() ? 'usa-input-error-label' : ''}`.trim();
  }

  /**
   * Style classes applied to the input element.
   */
  getInputClassNam() {
    if (this.props.disabled) {
      return 'disabled';
    }

    return `${this.state.focus ? 'usa-input-focus' : ''} ${this.isValid() ? 'usa-input-success' : ''
    }`.trim();
  }

  render() {
    const labelText = this.props.label;
    let label;
    if (labelText) {
      label = (
        <label className={this.labelClass()} htmlFor={this.state.uid} ref={this.label}>
          {this.props.label}
        </label>
      );
    }

    // autocomplete='off' doesn't work for most chrome settings for Chrome.
    // according to the WHATWG standard used by chrome and chrome ignores the HTML spec.
    // https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofilling-form-controls:-the-autocomplete-attribute
    // Chrome's opinion: https://bugs.chromium.org/p/chromium/issues/detail?id=468153#c164
    // Current open issue: https://bugs.chromium.org/p/chromium/issues/detail?id=587466
    let autoComplete = this.props.autocomplete ? 'on' : `new-${this.state.uid}`;
    if (this.props.type === 'password') {
      autoComplete = 'new-password'; // never autocomplete password fields
    }

    return (
      <div className={`hide-for-print ${this.getWrapperClassName()}`}>
        <AttributeAnchors attribute={this.props.attribute} />
        {label}

        {
          // see https://stackoverflow.com/questions/15738259/disabling-chrome-autofill
          this.props.type === 'password' && (
            <input
              type="password"
              style={{ display: 'none' }}
              name="fakepasswordfieldtodisablechromeautocomplete"
            />
          )
        }
        <DebugRender parent={this} />
        <input
          className={this.getInputClassNam()}
          id={this.state.uid}
          name={this.props.name}
          type={this.props.type}
          placeholder={this.props.placeholder}
          aria-label={this.props.ariaLabel || this.props.label || ariaLabel(this.input.current)}
          disabled={this.props.disabled}
          maxLength={this.props.maxlength}
          pattern={this.props.pattern}
          readOnly={this.props.readonly}
          autoCapitalize={this.props.autocapitalize ? 'on' : 'off'}
          autoCorrect={this.props.autocorrect ? 'on' : 'off'}
          autoComplete={autoComplete}
          spellCheck={this.props.spellcheck}
          value={this.state.value}
          onChange={this.handleChange} // eslint-disable-line @typescript-eslint/unbound-method
          onFocus={this.handleFocus} // eslint-disable-line @typescript-eslint/unbound-method
          onBlur={this.handleBlur} // eslint-disable-line @typescript-eslint/unbound-method
          onKeyDown={this.handleKeyDown} // eslint-disable-line @typescript-eslint/unbound-method
          onKeyPress={this.props.onKeyPress}
          onCopy={this.props.clipboard ? this.props.onCopy : this.disallowClipboard} // eslint-disable-line @typescript-eslint/unbound-method
          onCut={this.props.clipboard ? this.props.onCut : this.disallowClipboard} // eslint-disable-line @typescript-eslint/unbound-method
          onPaste={this.props.clipboard ? this.props.onPaste : this.disallowClipboard} // eslint-disable-line @typescript-eslint/unbound-method
          ref={this.input}
        />
        <div className="text-print print-only">{this.state.value}</div>
      </div>
    );
  }
}

Generic.defaultProps = {
  value: '',
  focus: false,
  error: false,
  valid: false,
  status: true,
  pattern: '.*',
  minlength: 0,
  maxlength: 255,
  clipboard: true,
  spellcheck: true,
  autocapitalize: true,
  autocorrect: true,
  autocomplete: false,
  ariaLabel: '',
  setToUpperCaseOnBlur: false,
  forceValidation: false,
  makeStateFromErrors: undefined,
  tabNext: () => { },
  tabBack: () => { },
  onError: (value, arr) => arr,
};

Generic.errors = [
  {
    code: 'required',
    func: (value, props) => {
      if (props.required) {
        return !!value;
      }
      return true;
    },
  },
  {
    code: 'length',
    func: (value, props) => {
      if (!value) {
        return null;
      }
      return (
        value.length >= parseInt(props.minlength, 10)
        && value.length <= parseInt(props.maxlength, 10)
      );
    },
  },
  {
    code: 'length.toolong',
    func: (value, props) => {
      if (!value) {
        return null;
      }
      return value.length <= parseInt(props.maxlength, 10);
    },
  },
  {
    code: 'length.tooshort',
    func: (value, props) => {
      if (!value) {
        return null;
      }
      return value.length >= parseInt(props.minlength, 10);
    },
  },
  {
    code: 'pattern',
    func: (value, props) => {
      if (!value) {
        return null;
      }

      const re = new RegExp(props.pattern);
      return re.test(value);
    },
  },
  {
    code: 'status',
    func: (value, props) => {
      if (!value) {
        return null;
      }

      return props.status;
    },
  },
];

export default stateFormErrorConnector(Generic);
